/**
 * File transformation utilities for converting API data to FileItem format
 */

import { MediaType } from '@qse/api'

export interface FileItem {
  id: string | number
  name: string
  type: string
  lastModified: string
  size?: number // in bytes
  path?: string
  parentId?: string // Parent folder ID for hierarchical structure
  isFavorite?: boolean
  isDir?: boolean
  createdAt: string
  trashedAt?: string | null // When the file was moved to trash
  mimeType: string
  status?: string // File status (e.g., 'COMPLETED', 'UPLOADING', 'PROCESSING', 'ENCRYPTING', 'AVAILABLE')
  mediaType?: MediaType
  owner?: string // Owner's display name
  isEncrypted?: boolean // Whether the file is encrypted and ready for use
  systemFolderType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
}

/**
 * Transforms API file data to FileItem format for FileExplorer component
 * @param apiFiles - API response containing file data
 * @returns Array of FileItem objects
 */
export const transformApiFiles = (apiFiles: any): FileItem[] => {
  if (!apiFiles?.data) return []

  return apiFiles.data.map((file: any) => ({
    id: file.id,
    name: file.name || file.path.split('/').pop() || 'Unknown',
    type: file.isDir ? 'folder' : file.mimeType || 'file',
    lastModified: file.updatedAt,
    size: file.sizeBytes || 0,
    path: file.path,
    parentId: file.parentId, // Include parentId from API data
    isFavorite: file.isFavorite,
    isDir: file.isDir,
    status: file.status,
    createdAt: file.createdAt,
    trashedAt: file.trashedAt || null,
    mimeType: file.mimeType,
    mediaType: (file.mediaType && MediaType[file.mediaType as keyof typeof MediaType]) || undefined,
    owner: file.owner || file.createdBy || undefined,
    isEncrypted: file.status === 'AVAILABLE' || file.status === 'COMPLETED' || file.isEncrypted,
    systemFolderType: file.systemFolderType,
  }))
}

/**
 * Transforms a single API file to FileItem format
 * @param file - Single API file object
 * @returns FileItem object
 */
export const transformSingleApiFile = (file: any): FileItem => {
  return {
    id: file.id,
    name: file.name || file.path.split('/').pop() || 'Unknown',
    type: file.isDir ? 'folder' : file.mimeType || 'file',
    lastModified: file.updatedAt,
    size: file.sizeBytes || 0,
    path: file.path,
    parentId: file.parentId, // Include parentId from API data
    isFavorite: file.isFavorite,
    isDir: file.isDir,
    status: file.status,
    createdAt: file.createdAt,
    trashedAt: file.trashedAt || null,
    mimeType: file.mimeType,
    mediaType: (file.mediaType && MediaType[file.mediaType as keyof typeof MediaType]) || undefined,
    owner: file.owner || file.createdBy || undefined,
    isEncrypted: file.status === 'AVAILABLE' || file.status === 'COMPLETED' || file.isEncrypted,
    systemFolderType: file.systemFolderType,
  }
}

/**
 * Transforms infinite query data to FileItem array
 * @param infiniteData - React Query infinite data
 * @returns Array of FileItem objects
 */
export const transformInfiniteQueryData = (infiniteData: any): FileItem[] => {
  if (!infiniteData?.pages) return []

  return infiniteData.pages.flatMap((page: any) => transformApiFiles(page))
}
